import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  IconButton,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { ArrowBack, Business } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const CreateOrganizationPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [maxUsers, setMaxUsers] = useState(50);
  const [maxProjects, setMaxProjects] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<any>(null);

  // Only superusers can access this page
  if (!user || user.role !== 'superuser') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Only superusers can create organizations.
        </Alert>
        <Button onClick={() => navigate('/projects')} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Container>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/auth/superuser/create-organization`,
        {
          name,
          subscription_tier: subscriptionTier,
          max_users: maxUsers,
          max_projects: maxProjects,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess(response.data);
      setName('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Create New Organization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Superuser only - Create organization for new customers
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            Organization Created Successfully!
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Organization Name:</strong> {success.organization.name}
            <br />
            <strong>Invite Code:</strong>{' '}
            <code style={{ fontSize: '1.1em', fontWeight: 600, padding: '2px 6px', background: '#f0f0f0', borderRadius: 4 }}>
              {success.organization.invite_code}
            </code>
            <br />
            <strong>ID:</strong> {success.organization.id}
            <br />
            <br />
            Share the invite code with the customer so they can register and join!
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(success.organization.invite_code);
              alert('Invite code copied to clipboard!');
            }}
            sx={{ mt: 2 }}
          >
            Copy Invite Code
          </Button>
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Business color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" fontWeight={600}>
            Organization Details
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Organization Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            helperText="Customer's company or firm name"
            disabled={loading}
            autoFocus
          />

          <TextField
            fullWidth
            select
            label="Subscription Tier"
            value={subscriptionTier}
            onChange={(e) => setSubscriptionTier(e.target.value)}
            margin="normal"
            helperText="Subscription level for this organization"
            disabled={loading}
          >
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
            <MenuItem value="enterprise">Enterprise</MenuItem>
          </TextField>

          <TextField
            fullWidth
            type="number"
            label="Max Users"
            value={maxUsers}
            onChange={(e) => setMaxUsers(parseInt(e.target.value))}
            margin="normal"
            helperText="Maximum users allowed in this organization"
            disabled={loading}
            inputProps={{ min: 1, max: 10000 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Max Projects"
            value={maxProjects}
            onChange={(e) => setMaxProjects(parseInt(e.target.value))}
            margin="normal"
            helperText="Maximum projects allowed in this organization"
            disabled={loading}
            inputProps={{ min: 1, max: 10000 }}
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/projects')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Create Organization'}
            </Button>
          </Box>
        </form>
      </Paper>

      {success && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setSuccess(null);
              setName('');
            }}
          >
            Create Another Organization
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default CreateOrganizationPage;
